/**
 * excelMappersBlack — parsers + mappers des fichiers Excel du produit "Black".
 *
 * Port fidèle de Black OS (`data-injections-management/index.jsx`), adapté au
 * schéma black_* NoiRock (multi-agence + AES). Les mappers produisent des rows
 * **cleartext** ; le chiffrement AES des diamants/bonus est fait côté Edge
 * Function `data-import-black` (R2 — la clé ne quitte jamais le serveur).
 *
 *   type1 (profils)    → black_creators
 *   type3 (journalier) → black_creator_daily
 *   type2 (mensuel)    → black_creator_monthly   (optionnel)
 *
 * Colonnes = export TikTok Backstage (EN + FR). type1 a son en-tête en 2e ligne.
 *
 * Gestion des départs (MAJ Black OS 2026) : un créateur dont
 *   type1.Relationship status === "The creator has left the agency"
 * est marqué status='left' (avant : détecté via type3.graduation_status).
 */
import * as XLSX from 'xlsx';

// ─── Constantes départ ─────────────────────────────────────────────────────
export const AGENCY_LEFT_STATUS_EN = 'The creator has left the agency';
export const AGENCY_LEFT_STATUS_FR = "Le/la créateur(trice) a quitté l'agence";

// ─── Helpers ──────────────────────────────────────────────────────────────
const safeNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(/[, ]/g, ''));
  return Number.isNaN(n) ? null : n;
};

const parseTikTokDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const cleaned = s.replace(/\s*\(UTC[^)]*\)\s*$/, '').trim();
  const tikTokMatch = cleaned.match(/^(\d{4}):(\d{2}):(\d{2})/);
  if (tikTokMatch) return `${tikTokMatch[1]}-${tikTokMatch[2]}-${tikTokMatch[3]}`;
  const isoMatch = cleaned.match(/(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  const d = new Date(cleaned);
  if (!Number.isNaN(d.getTime())) return d.toISOString().split('T')[0];
  return null;
};

// Join time → timestamptz ISO (garde l'heure si présente)
const parseJoinTimestamp = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const cleaned = s.replace(/\s*\(UTC[^)]*\)\s*$/, '').trim();
  const tikTokMatch = cleaned.match(/^(\d{4}):(\d{2}):(\d{2})(.*)/);
  if (tikTokMatch) return `${tikTokMatch[1]}-${tikTokMatch[2]}-${tikTokMatch[3]}${tikTokMatch[4] || ''}`;
  const fullMatch = cleaned.match(/^(\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}:\d{2})/);
  if (fullMatch) return fullMatch[1].replace(' ', 'T') + '+00:00';
  const dateMatch = cleaned.match(/(\d{4}-\d{2}-\d{2})/);
  if (dateMatch) return dateMatch[1];
  return null;
};

const parseContractRange = (raw) => {
  if (!raw) return { start: null, end: null };
  const parts = String(raw).trim().split('~');
  if (parts.length >= 2) {
    return { start: parseTikTokDate(parts[0].trim()), end: parseTikTokDate(parts[1].trim()) };
  }
  return { start: parseTikTokDate(String(raw).trim()), end: null };
};

// "12h 30m 38s" / "12:30" / "1.5" (heures) → secondes
const parseDurationSeconds = (raw) => {
  if (!raw) return 0;
  const s = String(raw).trim();
  const hms = s.match(/(\d+)\s*h(?:\s*(\d+)\s*m)?(?:\s*(\d+)\s*s)?/i);
  if (hms) return parseInt(hms[1], 10) * 3600 + (hms[2] ? parseInt(hms[2], 10) * 60 : 0) + (hms[3] ? parseInt(hms[3], 10) : 0);
  const colon = s.match(/^(\d+):(\d+)(?::(\d+))?/);
  if (colon) return parseInt(colon[1], 10) * 3600 + parseInt(colon[2], 10) * 60 + (colon[3] ? parseInt(colon[3], 10) : 0);
  const num = safeNum(s);
  return num != null ? Math.round(num * 3600) : 0; // suppose heures
};

const pctToNum = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = parseFloat(String(raw).replace('%', '').replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

const parseTopRegions = (raw) => {
  if (!raw) return null;
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
};

const normalizeGroup = (raw) => {
  const g = String(raw || '').trim();
  if (g === '' || g === 'Not in a group' || g === 'Dans aucun groupe') return null;
  return g;
};

// "2026-04" / "April 2026" / "202604" → "YYYY-MM"
const convertPeriod = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  const ym = s.match(/^(\d{4})[-/]?(\d{2})/);
  if (ym) return `${ym[1]}-${ym[2]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return null;
};

// ─── Mappers ────────────────────────────────────────────────────────────────

/** type1 (profils) → black_creators. Header en 2e ligne (range:1). */
export function mapType1Row(row, server) {
  const cleanRow = {};
  Object.keys(row).forEach((key) => {
    cleanRow[key.trim().replace(/:$/, '')] = row[key];
  });

  const tiktok_id = String(cleanRow['Creator ID'] || '').trim();
  if (!tiktok_id) return null;

  const contractRange = parseContractRange(cleanRow['Management relationship dates']);
  const renewedRange = parseContractRange(cleanRow['Renewed management relationship dates']);
  const tierLM = safeNum(cleanRow['Tier last month']);
  const relationship_status = String(cleanRow['Relationship status'] || '').trim() || null;

  return {
    tiktok_id,
    tiktok_username: String(cleanRow["Creator's username"] || '').trim() || null,
    server,
    group_label: normalizeGroup(cleanRow['Group']),
    tier: tierLM != null ? String(parseInt(tierLM, 10)) : null,
    tier_last_month: tierLM != null ? parseInt(tierLM, 10) : null,
    relationship_status,
    is_departed: relationship_status === AGENCY_LEFT_STATUS_EN || relationship_status === AGENCY_LEFT_STATUS_FR,
    subscription_status: String(cleanRow['Subscription status'] || '').trim() || null,
    invitation_type: String(cleanRow['Invitation type'] || '').trim() || null,
    join_time: parseJoinTimestamp(cleanRow['Join time']),
    days_since_joining: safeNum(cleanRow['Days since joining']),
    contract_start: contractRange.start,
    contract_end_date: renewedRange.end || contractRange.end,
    last_live_date: parseTikTokDate(cleanRow['Last LIVE'] || cleanRow['Last live'] || cleanRow['last live'] || null),
  };
}

/** type3 (journalier) → black_creator_daily. */
export function mapType3Row(row, server) {
  const get = (en, fr) => row[en] ?? (fr ? row[fr] : undefined) ?? null;

  const rawPeriod = String(get('Data period', 'Période des données') || '').trim();
  let date = null;
  if (rawPeriod) {
    // "2026-05-24 ~ 2026-05-24" → on garde le début ; "20260524" → ISO
    const startPart = rawPeriod.split('~')[0].trim();
    if (/^\d{8}$/.test(startPart)) date = `${startPart.slice(0, 4)}-${startPart.slice(4, 6)}-${startPart.slice(6, 8)}`;
    else date = parseTikTokDate(startPart) || startPart.slice(0, 10);
  }

  const tiktok_id = String(get('Creator ID', 'ID créateur(trice)') || '').trim();
  if (!tiktok_id || !/^\d+$/.test(tiktok_id)) return null; // rejette "The creator has quit the network"

  return {
    tiktok_id,
    server,
    snapshot_date: date,
    tiktok_username: String(get("Creator's username", "Nom d'utilisateur du/de la créateur(trice)") || '').trim() || null,
    group_label: normalizeGroup(get('Group', 'Groupe')),

    // Activity (cleartext)
    valid_go_live_days: safeNum(get('Valid go LIVE days', 'Jours de passage en LIVE valides')) || 0,
    live_duration_seconds: parseDurationSeconds(String(get('LIVE duration', 'Durée de LIVE') || '')) || 0,
    new_followers: safeNum(get('New followers', 'Nouveaux followers')) || 0,
    live_streams: safeNum(get('LIVE streams', 'Diffusions LIVE')) || 0,
    matches: safeNum(get('Matches', 'Matchs')) || 0,

    // Diamonds (→ chiffrés côté Edge Function)
    diamonds: safeNum(get('Diamonds', 'Diamants')) || 0,
    diamonds_from_matches: safeNum(get('Diamonds from matches', 'Diamants des matchs')),
    diamonds_from_multiguest_total: safeNum(get('Diamonds from multi-guest', 'Diamants du mode multi-invité')),
    diamonds_from_multiguest_host: safeNum(get('Diamonds from multi-guest (as host)')),
    diamonds_from_multiguest_guest: safeNum(get('Diamonds from multi-guest (as guest)')),
    overseas_diamonds: safeNum(get('Overseas Diamonds')),

    // Last month (avril export)
    diamonds_last_month: safeNum(get('Diamonds last month')),
    live_duration_lm_seconds: get('LIVE duration (hours) last month') != null
      ? Math.round((safeNum(get('LIVE duration (hours) last month')) || 0) * 3600) : null,
    valid_go_live_days_lm: safeNum(get('Valid go LIVE days last month')),
    new_followers_lm: safeNum(get('New followers last month')),
    live_streams_lm: safeNum(get('LIVE streams last month')),

    // % achieved
    diamonds_pct_achieved: pctToNum(get('Diamonds - Percentage achieved')),
    live_duration_pct_achieved: pctToNum(get('LIVE duration - Percentage achieved')),
    valid_days_pct_achieved: pctToNum(get('Valid go LIVE days - Percentage achieved')),
    followers_pct_achieved: pctToNum(get('New followers - Percentage achieved')),
    streams_pct_achieved: pctToNum(get('LIVE streams - Percentage achieved')),

    // vs last month
    diamonds_vs_lm_pct: pctToNum(get('Diamonds - vs. last month')),
    live_duration_vs_lm_pct: pctToNum(get('LIVE duration - vs. last month')),
    valid_days_vs_lm_pct: pctToNum(get('Valid go LIVE days - vs. last month')),
    followers_vs_lm_pct: pctToNum(get('New followers - vs. last month')),
    streams_vs_lm_pct: pctToNum(get('LIVE streams - vs. last month')),

    // Tier / status texts
    graduation_status_text: String(get('Graduation status', "Statut d'évolution") || '').trim() || null,
    tier_status_text: String(get('Tier status', "Statut de l'échelon") || '').trim() || null,
    status_text: String(get('Status') || '').trim() || null,
    is_new_live_creator: String(get('New LIVE creators') || '').trim() || null,

    // Overseas
    overseas_diamond_pct: safeNum(get('Overseas Diamond percentage')),
    top_regions_json: parseTopRegions(get('Top contributing overseas regions')),

    join_time: parseJoinTimestamp(get('Join time', "Date d'établissement de la relation")),
    days_since_joining: safeNum(get('Days since joining', "Jours depuis l'adhésion")),
  };
}

/** type2 (mensuel) → black_creator_monthly. Optionnel. */
export function mapType2Row(row, server, periodOverride) {
  const rawId = row['Creator ID'];
  let tiktok_id = String(rawId ?? '').trim();
  if (!tiktok_id) return null;
  const period = periodOverride || convertPeriod(row['Data Month']);
  if (tiktok_id === '0' || rawId === 0) {
    const handle = String(row['Handle'] || row['Creator nickname'] || '').trim();
    tiktok_id = `LEFT_${handle || 'unknown'}_${period}_${server}`;
  }
  const bonus = parseFloat(row['Estimated bonus']) || 0;
  const charge = parseFloat(row['charge']) || 0;
  return {
    tiktok_id,
    tiktok_username: String(row['Handle'] || '').trim() || null,
    display_name: String(row['Creator nickname'] || '').trim() || null,
    server,
    period,
    diamonds: parseInt(row['Diamonds'], 10) || 0,
    live_days: parseInt(row['Valid days(d)'], 10) || 0,
    live_hours: parseFloat(row['LIVE duration(h)']) || 0,
    bonus,
    charge,
    net_agency: bonus - charge,
    activeness_bonus: parseFloat(row['Estimated bonus - Activeness task task']) || 0,
    incremental_bonus: parseFloat(row['Estimated bonus - Incremental revenue task task']) || 0,
    is_violative: row['Is violative creators'] === true
      || String(row['Is violative creators']).toLowerCase() === 'true'
      || String(row['Is violative creators']).toLowerCase() === 'yes'
      || row['Is violative creators'] === 1,
    is_rookie: row['The creator was Rookie at the time of first joining'] === 'Yes',
    group_label: normalizeGroup(row['Group']),
    group_manager: String(row['Group manager'] || row['Creator Network manager'] || '').trim() || null,
    tier: safeNum(row['Tier']) != null ? String(parseInt(safeNum(row['Tier']), 10)) : null,
  };
}

// ─── XLSX parsing ─────────────────────────────────────────────────────────
export async function parseExcel(file, options = {}) {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: 'array' });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  return XLSX.utils.sheet_to_json(firstSheet, { defval: '', ...options });
}

/**
 * Parse + map un fichier en rows cleartext prêtes pour l'Edge Function.
 * type1 a son en-tête sur la 2e ligne → range:1.
 */
export async function parseAndMap(file, fileType, server, periodLabel) {
  const rows = await parseExcel(file, fileType === 'type1' ? { range: 1 } : {});
  return rows
    .map((r) => {
      if (fileType === 'type1') return mapType1Row(r, server);
      if (fileType === 'type2') return mapType2Row(r, server, periodLabel);
      if (fileType === 'type3') return mapType3Row(r, server);
      return null;
    })
    .filter(Boolean);
}
